import { GAEvent, GAEventNames } from 'game/analytics/events';
import { fadeUI } from 'game/core/Playable2DRootContainer';
import { EventName } from 'game/events/eventsName';
import { GameEvent } from 'game/events/gameEvent';
import { QuestManager } from 'game/quests/QuestManager';
import { ActionType } from 'game/quests/types';
import { ADS_RECEIVE_WAND_COUNTER, DEFAULT_HINT_COUNTER, DEFAULT_UNDO_COUNTER, Store } from 'game/store/gameStore';
import { Popups } from 'ui/types/popups';
import { GameUI } from './GameUI';
let adsTimer: number | null = null;

export type AdsPlacement = 'undo' | 'hint' | 'wand' | 'mission' | 'new-missions-request';
export class AdsManager {
    place: AdsPlacement | null = null;
    adGranted = false;
    bannerTrigger = 'New Game' as 'Restart' | 'New Game' | 'Win Popup';
    adsReady = false;
    showAdsCb: (() => void) | null = null;
    constructor() {
        GameEvent.add(EventName.SHOW_ADS, this.showAds.bind(this));

        this.onAdsLoaded = this.onAdsLoaded.bind(this);

        addEventListener('gptScriptLoaded', this.onAdsLoaded);

        addEventListener('dismissRewardedAd', this.dismissRewardedAd.bind(this));
        addEventListener('rewardedSlotGranted', this.rewardedSlotGranted.bind(this));

        addEventListener('adBreakAfter', this.adBreakAfter.bind(this));
        addEventListener('adBreakBefore', this.adBreakBefore.bind(this));
        addEventListener('adBreakDone', this.adBreakDone.bind(this));
        addEventListener(
            'newGame',
            () => {
                this.showBannerAds('New Game');
            },
            false
        );
        addEventListener(
            'newWinGame',
            () => {
                this.showBannerAds('Win Popup');
            },
            false
        );
        addEventListener(
            'restartGame',
            () => {
                this.showBannerAds('Restart');
            },
            false
        );
        addEventListener('noSlotsRewardedAd', this.noSlotsRewardedAd.bind(this), false);
        GameEvent.add(EventName.CLOSE_MODAL, this.onCloseModal.bind(this));
        GameEvent.add(EventName.OPEN_MODAL, this.onOpenModal.bind(this));


        
        this.onAdsLoaded();
    }
    onAdsLoaded() {
        removeEventListener('gptScriptLoaded', this.onAdsLoaded);
        this.adsReady = true;
        if (this.showAdsCb) {
            this.showAdsCb();
            this.showAdsCb = null;
        }
    }
    adBreakAfter() {
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        GAEvent(GAEventNames.ADS_BREAK_DONE, {
            trigger: this.bannerTrigger,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        if (Store.gameOver) return;
        Store.resumeGame();
    }
    adBreakBefore() {
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        GAEvent(GAEventNames.ADS_BREAK_START, {
            trigger: this.bannerTrigger,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        if (Store.gameOver) return;
        Store.resumeGame();
    }

    adBreakDone() {
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        if (Store.gameOver) return;
        Store.resumeGame();
    }

    showBannerAds(place: 'Restart' | 'New Game' | 'Win Popup' = 'New Game') {
        this.bannerTrigger = place;
        if (adsTimer) clearTimeout(adsTimer);
        // GAEvent(GAEventNames.ADS_BREAK_POPUP, { // we show instantly for now
        //     trigger: place,
        //     type: Store.getGameTypeText(),
        //     difficulty: Store.getDifficultyText(),
        // });

        Store.pauseGame();
        switch (place) {
            case 'Restart':
                dispatchEvent(new CustomEvent('showAdBanner', { detail: { type: 'start', name: 'restart' } }));
                break;
            case 'New Game':
                dispatchEvent(new CustomEvent('showAdBanner', { detail: { type: 'start', name: 'new-game' } }));
                break;
            case 'Win Popup':
                dispatchEvent(new CustomEvent('showAdBanner', { detail: { type: 'next', name: 'win-game' } }));
                break;
        }
    }
    showAds({ place, isFallback = false, onNoFallbackAd = undefined }: { place: AdsPlacement, isFallback?: boolean, onNoFallbackAd?: () => void }) {
        
        if (!isFallback)  {
            Store.startGame();

            this.adGranted = false;
            this.place = place;
            GAEvent(GAEventNames.ADS_REWARDER_CLICK, {
                trigger: place,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
    
            Store.pauseGame();
            GameEvent.emit(EventName.SHOW_LOADER);
            GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, Infinity);
            fadeUI(0.5);

            if (this.adsReady) {
                dispatchEvent(new CustomEvent('showAd', { detail: { place } }, ));
            }
            else {
                this.showAdsCb = () => {
                    dispatchEvent(new CustomEvent('showAd', { detail: { place } }));
                }
            };
        } else {
            dispatchEvent(new CustomEvent('showAdsenseRewarded', { detail: { place, onDone: () => {
                this.rewardedSlotGranted({ detail: { place } });
                GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
            }, adBreakDone: (placementInfo: any) => {
                GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
                if (['notReady', 'timeout', 'invalid', 'error', 'noAdPreloaded', 'frequencyCapped', 'other'].includes(placementInfo.breakStatus as string)) {
                    onNoFallbackAd?.();
                } else {
                    this.dismissRewardedAd();
                }
            } } }));
        }
    }
    dismissRewardedAd() {
        GameEvent.emit(EventName.HIDE_LOADER, 0);
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        fadeUI(1, 0);
        GameEvent.emit(EventName.ADS_CLOSED);
        Store.resumeGame();
        if (!this.adGranted)
            GAEvent(GAEventNames.ADS_REWARDER_SKIP, {
                trigger: this.place || 'unknown',
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
            });
    }

    rewardedSlotGranted(e: { detail: { place: AdsPlacement } }) {
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        GAEvent(GAEventNames.ADS_REWARDER_RECEIVE, {
            trigger: e.detail.place,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });
        this.adGranted = true;
        switch (e.detail.place) {
            case 'undo':
                Store.setUndoCounter(DEFAULT_UNDO_COUNTER);
                break;
            case 'hint':
                Store.setHintCounter(DEFAULT_HINT_COUNTER);
                break;
            case 'wand':
                Store.setWandCounter(ADS_RECEIVE_WAND_COUNTER);
                break;
            case 'mission':
                GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.AD, payload: { adWatched: 1 }, currentProgress: 1 });
                break;
            case 'new-missions-request':
                QuestManager.setupNewMission();
                break;
        }
        GameEvent.emit(EventName.ADS_GRANTED, e.detail.place);
    }

    noSlotsRewardedAd(e: { detail: { place: AdsPlacement; reason: string; event?: unknown } }) {
        this.showAds({ place: e.detail.place, isFallback: true, onNoFallbackAd: () => {
            GameEvent.emit(EventName.HIDE_LOADER, 0);
            GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
            fadeUI(1);
            GAEvent(GAEventNames.ADS_REWARDER_ERROR, {
                trigger: e.detail.place,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
                slot: JSON.stringify(e.detail.event.slot),
                isBackfill: JSON.stringify(e.detail.event.isBackfill),
            });
            this.adGranted = true;
            switch (e.detail.place) {
                case 'undo':
                case 'hint':
                case 'wand':
                case 'new-missions-request':
                    this.showNoAdsModal();
                    break;
                case 'mission':
                    GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.AD, payload: { adWatched: 1 }, currentProgress: 1 });
                    break;
            }

            GameEvent.emit(EventName.ADS_GRANTED, e.detail.event.slot);
            this.dismissRewardedAd();
        } });
    }

    showNoAdsModal() {
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        GameUI.showPopup(Popups.NO_ADS_POPUP);

        window.dataLayer.push({
        event: "stories_no_ads_banner_view",
        });

        const noAdsModal = document.getElementById("no_ads");
        if (noAdsModal) {
        const closeButton =
            noAdsModal.querySelector(".modals_area-btn");
        if (closeButton) {
            const closeHandler = () => {
            window.dataLayer.push({
                event: "stories_no_ads_banner_close",
            });
            // Remove the event listener after it's triggered
            closeButton.removeEventListener("click", closeHandler);
            };
            closeButton.addEventListener("click", closeHandler);
        }
        }
    }
    adBlockerHandler() {
        GameEvent.emit(EventName.HIDE_LOADER, 0);
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 0);
        fadeUI(1, 0);
        GameEvent.emit(EventName.ADS_CLOSED);
        Store.resumeGame();
    }
    onCloseModal({ id }: { id: string; confirm: boolean }) {
        switch (id) {
            case 'adblocker':
                GAEvent(GAEventNames.AD_BLOCKER_POPUP_CLOSE, {
                    trigger: this.place || 'unknown',
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                });
                this.adBlockerHandler();
                break;
        }
    }
    onOpenModal({ id }: { id: string }) {
        switch (id) {
            case 'adblocker':
                GAEvent(GAEventNames.AD_BLOCKER_POPUP_SHOW, {
                    trigger: this.place || 'unknown',
                    type: Store.getGameTypeText(),
                    difficulty: Store.getDifficultyText(),
                });
                break;
        }
    }
}
